import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { 
  FiCheckCircle, FiCircle, FiClock, FiAlertTriangle, 
  FiDatabase, FiSettings, FiActivity, FiRefreshCw,
  FiInfo, FiArrowRight, FiShield, FiTarget, FiZap, FiHelpCircle
} from 'react-icons/fi';
import { demoSteps, dummyExercises, faqData } from '../data/dummyData';
import Recovery from './Recovery';

export default function Demo() {
  const [activeStep, setActiveStep] = useState('setup');
  const [demoTab, setDemoTab] = useState('overview'); // overview, flow, data, states, faq
  const [simulatedState, setSimulatedState] = useState('success');

  const renderSimulatedState = (type, title, message) => {
    const states = {
      success: { color: 'text-lime-500', border: 'border-lime-500/30', bg: 'bg-lime-500/5', icon: FiCheckCircle },
      warning: { color: 'text-yellow-500', border: 'border-yellow-500/30', bg: 'bg-yellow-500/5', icon: FiAlertTriangle },
      error: { color: 'text-red-500', border: 'border-red-500/30', bg: 'bg-red-500/5', icon: FiShield },
      loading: { color: 'text-blue-500', border: 'border-blue-500/30', bg: 'bg-blue-500/5', icon: FiRefreshCw },
      empty: { color: 'text-gray-500', border: 'border-carbon-600', bg: 'bg-carbon-800/50', icon: FiCircle }
    };
    const s = states[type];
    return (
      <motion.div 
        layout
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`${s.bg} ${s.border} border p-4 rounded-xl flex items-start gap-3`}
      >
        <SafeIcon icon={s.icon} className={`${s.color} text-xl mt-1 ${type === 'loading' ? 'animate-spin' : ''}`} />
        <div>
          <div className={`text-sm font-bold uppercase font-mono ${s.color}`}>{title}</div>
          <div className="text-xs text-gray-400 mt-1">{message}</div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-12">
      {/* Header & Tabs */}
      <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-6">
        <div>
          <h2 className="text-3xl font-bold text-white tracking-tighter flex items-center gap-3">
            <SafeIcon icon={FiInfo} className="text-lime-500" />
            SYSTEM_OPERATIONS.HUB
          </h2>
          <p className="text-gray-500 font-mono text-sm mt-1 uppercase tracking-widest">Interface Simulation & Knowledge Base</p>
        </div>
        <div className="flex bg-carbon-800 p-1 rounded-lg border border-carbon-700 overflow-x-auto whitespace-nowrap scrollbar-hide">
          {['overview', 'flow', 'data', 'states', 'faq'].map((t) => (
            <button
              key={t}
              onClick={() => setDemoTab(t)}
              className={`px-4 py-2 rounded-md text-[10px] font-mono uppercase tracking-widest transition-all ${demoTab === t ? 'bg-lime-500 text-carbon-900 font-bold' : 'text-gray-400 hover:text-white'}`}
            >
              {t.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {demoTab === 'overview' && (
          <motion.div 
            key="overview"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            <div className="space-y-6">
              <div className="glass-panel p-8 border-l-4 border-l-lime-500">
                <h3 className="text-2xl font-bold text-white mb-4 tracking-tight">The Performance Orchestration Layer</h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  FitSync Lab is designed for active users who demand precision in their training. We solve the problem of fragmented biometric data by unifying wearable telemetry with high-fidelity strength programming.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-carbon-900/50 p-4 rounded-lg border border-carbon-700">
                    <div className="text-lime-500 font-bold text-sm mb-1 uppercase font-mono">Target User</div>
                    <p className="text-xs text-gray-500">Data-driven athletes building consistent, high-volume routines.</p>
                  </div>
                  <div className="bg-carbon-900/50 p-4 rounded-lg border border-carbon-700">
                    <div className="text-lime-500 font-bold text-sm mb-1 uppercase font-mono">Core Outcome</div>
                    <p className="text-xs text-gray-500">Optimized recovery ratios and injury-free hypertrophy cycles.</p>
                  </div>
                </div>
              </div>
              <div className="glass-panel p-6">
                <h4 className="text-white font-bold mb-4 flex items-center gap-2"><SafeIcon icon={FiZap} className="text-lime-500" /> Key Features</h4>
                <ul className="space-y-3 text-sm text-gray-400">
                  <li className="flex items-center gap-3"><SafeIcon icon={FiCheckCircle} className="text-lime-500" /> Adaptive Load Scaling via CNS monitoring</li>
                  <li className="flex items-center gap-3"><SafeIcon icon={FiCheckCircle} className="text-lime-500" /> Kinetic Sequence Constructor for routine building</li>
                  <li className="flex items-center gap-3"><SafeIcon icon={FiCheckCircle} className="text-lime-500" /> Real-time Telemetry Uplink during active sets</li>
                </ul>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute inset-0 bg-lime-500/10 blur-3xl rounded-full group-hover:bg-lime-500/20 transition-all" />
              <div className="relative glass-panel h-full p-8 flex flex-col justify-center text-center">
                <SafeIcon icon={FiActivity} className="text-7xl text-lime-500 mx-auto mb-6 text-glow" />
                <h3 className="text-xl font-bold text-white mb-2">Control Room Ready</h3>
                <p className="text-sm text-gray-500 max-w-sm mx-auto">
                  Experience a performance dashboard that feels like a mission control center. No distractions, just actionable data.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {demoTab === 'flow' && (
          <motion.div 
            key="flow"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8"
          >
            <div className="lg:col-span-4 space-y-3">
              <h3 className="text-gray-400 text-[10px] font-mono uppercase tracking-widest mb-6 px-2">Operational_Workflow</h3>
              {demoSteps.map((step) => (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-all duration-300 relative group
                    ${activeStep === step.id ? 'bg-carbon-800 border-lime-500/50 glow-lime' : 'bg-carbon-900/50 border-carbon-800 hover:border-carbon-600'}
                  `}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[10px] font-mono ${activeStep === step.id ? 'text-lime-500' : 'text-gray-500'}`}>{step.title}</span>
                    <SafeIcon icon={activeStep === step.id ? FiArrowRight : FiCircle} className={activeStep === step.id ? 'text-lime-500' : 'text-carbon-700'} />
                  </div>
                  <p className={`text-xs ${activeStep === step.id ? 'text-white' : 'text-gray-500'}`}>{step.desc}</p>
                </button>
              ))}
            </div>

            <div className="lg:col-span-8 glass-panel p-8 relative overflow-hidden flex flex-col justify-center min-h-[450px]">
              <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                <SafeIcon icon={FiActivity} className="text-[200px] text-lime-500" />
              </div>
              <div className="relative z-10">
                <div className="text-lime-500 font-mono text-[10px] uppercase mb-4 tracking-widest">Live_Simulator / Try this flow</div>
                <AnimatePresence mode="wait">
                  <motion.div 
                    key={activeStep}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-6"
                  >
                    {activeStep === 'setup' && (
                      <div className="space-y-6">
                        <h3 className="text-3xl font-bold text-white mb-4">Baseline Calibration</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 bg-carbon-900 border border-carbon-700 rounded-lg">
                            <label className="text-[10px] text-gray-500 font-mono uppercase block mb-1">Movement Capacity</label>
                            <div className="text-xl text-white font-bold">Level 7 (Elite)</div>
                          </div>
                          <div className="p-4 bg-carbon-900 border border-carbon-700 rounded-lg">
                            <label className="text-[10px] text-gray-500 font-mono uppercase block mb-1">Focus Vector</label>
                            <div className="text-xl text-white font-bold">Hypertrophy</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {activeStep === 'build' && (
                      <div className="space-y-4">
                        <h3 className="text-3xl font-bold text-white">Sequence Constructor</h3>
                        <div className="space-y-2">
                           {dummyExercises.slice(0, 4).map(ex => (
                             <div key={ex.id} className="flex items-center justify-between p-3 bg-carbon-900/80 border border-carbon-700 rounded-lg">
                                <span className="text-white font-medium text-sm">{ex.name}</span>
                                <div className="flex gap-4">
                                  <span className="text-gray-500 font-mono text-[10px] uppercase">LOAD: {ex.load}</span>
                                  <span className="text-lime-500 font-mono text-[10px] uppercase">VOL: {ex.volume}</span>
                                </div>
                             </div>
                           ))}
                        </div>
                      </div>
                    )}
                    {!['setup', 'build'].includes(activeStep) && (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <SafeIcon icon={FiClock} className="text-5xl text-carbon-700 mb-4" />
                        <p className="text-gray-500 font-mono text-sm uppercase tracking-widest">Module_Simulation_Active</p>
                        <p className="text-xs text-gray-600 mt-2">Awaiting sequence commit from primary operative.</p>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}

        {demoTab === 'data' && (
          <motion.div 
            key="data"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            <div className="glass-panel p-6">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <SafeIcon icon={FiDatabase} className="text-lime-500" />
                Exercise Node Archive
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="text-[10px] font-mono text-gray-500 uppercase border-b border-carbon-700">
                    <tr>
                      <th className="pb-2">Movement</th>
                      <th className="pb-2">Intensity</th>
                      <th className="pb-2">Volume</th>
                      <th className="pb-2 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    {dummyExercises.map(ex => (
                      <tr key={ex.id} className="border-b border-carbon-800/50 hover:bg-carbon-800/30 transition-colors">
                        <td className="py-3 font-medium">{ex.name}</td>
                        <td className="py-3 font-mono text-[10px]">{ex.load}</td>
                        <td className="py-3 font-mono text-[10px]">{ex.volume}</td>
                        <td className="py-3 text-right">
                          <span className={`text-[9px] px-2 py-0.5 rounded font-mono uppercase ${ex.difficulty === 'Advanced' ? 'bg-red-500/10 text-red-400' : 'bg-lime-500/10 text-lime-500'}`}>
                            {ex.difficulty}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-6">
              <Recovery />
              <div className="glass-panel p-6 bg-gradient-to-br from-carbon-800 to-carbon-900">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiTarget} /> System Metrics Snapshot
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-carbon-900 p-4 rounded-lg border border-carbon-700">
                    <div className="text-[10px] text-gray-500 font-mono uppercase mb-1">Total Tonnage</div>
                    <div className="text-xl font-bold text-white">42.8k <span className="text-[10px] text-gray-600 font-normal">KG</span></div>
                  </div>
                  <div className="bg-carbon-900 p-4 rounded-lg border border-carbon-700">
                    <div className="text-[10px] text-gray-500 font-mono uppercase mb-1">Intensity Avg</div>
                    <div className="text-xl font-bold text-lime-500">84%</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {demoTab === 'states' && (
          <motion.div 
            key="states"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            <div className="flex flex-wrap gap-2 p-2 bg-carbon-800 rounded-lg border border-carbon-700">
              {['success', 'loading', 'warning', 'error', 'empty'].map(s => (
                <button
                  key={s}
                  onClick={() => setSimulatedState(s)}
                  className={`px-4 py-2 rounded font-mono text-[10px] uppercase tracking-widest transition-all ${simulatedState === s ? 'bg-white text-carbon-900 font-bold' : 'text-gray-400 hover:text-white'}`}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {simulatedState === 'success' && renderSimulatedState('success', 'Uplink Established', 'Data sequence committed to the central core successfully. Metrics are stable.')}
              {simulatedState === 'loading' && renderSimulatedState('loading', 'Syncing Parameters', 'Retrieving real-time movement telemetry from wearable nodes. Please hold.')}
              {simulatedState === 'warning' && renderSimulatedState('warning', 'Load Threshold Warning', 'Current volume trajectory exceeds recovery baseline by 15%. Recommend scaling.')}
              {simulatedState === 'error' && renderSimulatedState('error', 'Integrity Breach', 'Unable to verify biometric encryption keys. Re-authentication protocol required.')}
              {simulatedState === 'empty' && renderSimulatedState('empty', 'Awaiting Sequence', 'No active routines detected in the current temporal window. Constructor idle.')}
            </div>
          </motion.div>
        )}

        {demoTab === 'faq' && (
          <motion.div 
            key="faq"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto space-y-4"
          >
            {faqData.map((item, i) => (
              <div key={i} className="glass-panel p-6 hover:border-carbon-500 transition-colors">
                <div className="flex gap-4">
                  <SafeIcon icon={FiHelpCircle} className="text-lime-500 text-xl shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-bold mb-2 tracking-tight">{item.q}</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">{item.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer Credit */}
      <div className="pt-12 mt-12 border-t border-carbon-800 text-center">
        <p className="text-[10px] font-mono text-gray-700 uppercase tracking-[0.4em] font-bold">
          Made by LingLink AI and DigLab AI
        </p>
      </div>
    </div>
  );
}